/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { HStack, IconButton, Input, InputGroup, InputLeftElement, Stack, Text } from '@chakra-ui/core';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { RecruiterListTable } from './RecruiterListTable';
import { SelectPositionStatus } from './SelectPositionStatus';
import { Position } from '../types';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';

interface SelectedPositionViewProps {
  position: Position;
}

function SelectedPositionViewComponent({ position }: SelectedPositionViewProps) {
  const { t } = useTranslation('moniterByRecruiters');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('all');
  const { setSelectedPosition, setRecruiterFilter } = useStoreActions((state) => state.moniterByRecruiters);
  const { statusUpdatingRecruiterId: updatingRecruiterId, recruiterFilters } = useStoreState((s) => s.moniterByRecruiters);

  const handleStatusChange = React.useCallback((status: string) => {
    setSelectedStatus(status);
    // eslint-disable-next-line
  }, []);

  const handleSearchTermChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRecruiterFilter({ key: 'search', value: event.target.value });
  }, []);

  const handleClose = React.useCallback(() => {
    if (updatingRecruiterId) return;
    setSelectedPosition(null);
  }, [setSelectedPosition]);

  React.useEffect(() => {
    setRecruiterFilter({ key: 'search', value: '' });
    return () => {
      setRecruiterFilter({ key: 'search', value: '' });
    };
  }, []);

  return (
    <Stack
      sx={{
        minHeight: 0,
        height: '100%',
        marginTop: '1px',
        padding: 6,
        backgroundColor: 'white',
        borderLeft: '1px solid #f0f0f0',
        borderRadius: 'md',
        borderTopLeftRadius: 0,
        maxHeight: 'calc(100vh - 64px - 4rem - 45px)',
      }}
      spacing={4}
    >
      <HStack justify="space-between" align="flex-start">
        <HStack align="flex-start" spacing={4}>
          <Stack flex={1} spacing={0}>
            <Text fontSize="lg" fontWeight="600" lineHeight={1}>
              {position.customName}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {position.businessName}
            </Text>
          </Stack>
        </HStack>
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="close"
          onClick={handleClose}
          _hover={{ cursor: updatingRecruiterId ? 'progress' : 'pointer' }}
        >
          <IoClose size={20} />
        </IconButton>
      </HStack>
      <Stack justify="space-between" spacing={4}>
        <HStack flexGrow={1}>
          <InputGroup position="relative" background="white" borderRadius="md" maxWidth={300}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              data-testid="search-recruiters"
              placeholder={t('positionRecruiters.searchPlaceholder')}
              size="md"
              borderRadius="md"
              value={recruiterFilters.search}
              onChange={handleSearchTermChange}
            />
          </InputGroup>
        </HStack>
        <HStack>
          <SelectPositionStatus onSelect={handleStatusChange} />
          <WatchAndFollowingCount position={position} />
        </HStack>
      </Stack>
      <RecruiterListTable selectedStatus={selectedStatus} />
    </Stack>
  );
}

function WatchAndFollowingCount({ position }: { position: Position }) {
  const { t } = useTranslation('moniterByRecruiters');

  const watchedCount = position?.watchedRecruiters?.length || 0;
  const followedCount = position?.assignedRecruiters?.length || 0;
  return (
    <Stack flexShrink={0} spacing={0}>
      <HStack spacing={1}>
        <Text fontSize="sm">{`${t('positionRecruiters.count.watching')}`}</Text>
        <Text fontSize="sm" fontWeight="600">
          {`${watchedCount} ${t(watchedCount === 1 ? 'positionRecruiters.count.count' : 'positionRecruiters.count.counts')}`}
        </Text>
      </HStack>
      <HStack spacing={1}>
        <Text fontSize="sm">{`${t('positionRecruiters.count.following')}`}</Text>
        <Text fontSize="sm" fontWeight="600">
          {`${followedCount} ${t(followedCount === 1 ? 'positionRecruiters.count.count' : 'positionRecruiters.count.counts')}`}
        </Text>
      </HStack>
    </Stack>
  );
}

export const SelectedPositionView = React.memo(SelectedPositionViewComponent);
